import React from "react";
import { Typography, Button as MuiButton, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as yup from "yup";
import { Link } from "react-router-dom";
import { useMutation } from "@apollo/client";

import { EmailSvg } from "../../../assets/svg/EmailSvg";
import { Button } from "../../Button";
import { AppSubmitButton, AppTextField } from "../../apps";
import { APP_ROUTES } from "../../../constants/app_routes";
import { text } from "../../../text";
import { theme } from "../../../constants";
import { RESEND_OTP, VERIFY_EMAIL } from "../../../graphql/mutations/profile";
import { showErrorToast, showSuccessToast } from "../../apps/AppError";
import { useQueryProfile } from "../../../graphql/apollo/useQuery";
import { dispatch } from "../../../store/store";
import { profileActions } from "../../../store/slices/profileSlice";

interface StepProps {
  email?: string;
  backToProfile: () => void;
}

const EmailVerificationStep: React.FC<StepProps> = ({
  email,
  backToProfile,
}) => {
  const { t } = useTranslation();
  const onInitSteps = (data: any) => {
    const { myProfile } = data;
    dispatch(
      profileActions.initStep({
        isVerifiedEmail: myProfile.isVerifiedEmail,
        isUpdatePersonalData: myProfile.isUpdatePersonalData,
        isVerifiedIdentity: myProfile.isVerifiedIdentity,
      })
    );
  };
  const { refetch, myProfile } = useQueryProfile((data: any) =>
    onInitSteps(data)
  );
  let isVerify = myProfile?.isVerifiedEmail;
  const validationSchema = yup.object({
    email: yup.string(),
    password: yup.string(),
  });

  const [resendOtp] = useMutation(RESEND_OTP, {
    onError: (err) => {
      showErrorToast(err.message);
    },
  });

  const [verifyEmail] = useMutation(VERIFY_EMAIL, {
    onCompleted: (data) => {
      if (data.verifyEmail) {
        showSuccessToast();
        refetch();
        dispatch(profileActions.personalDataStep());
      }
    },
    onError: (err) => {
      showErrorToast(err.message);
    },
  });

  const handleSubmit = (data: any) =>
    verifyEmail({ variables: { code: data.code } });

  const handleContinue = () => dispatch(profileActions.personalDataStep());

  return (
    <div className="verification-step">
      <Typography variant="h5" sx={{ textAlign: "center" }}>
        {t("emailConfirmation")}
      </Typography>
      <div>
        <EmailSvg style={{ marginLeft: "auto", marginRight: "auto" }} />
      </div>
      {isVerify ? (
        <div>
          <Typography color="green" sx={{ textAlign: "center", mt: 2 }}>
            {t("email")} {email}
          </Typography>
          <Typography sx={{ textAlign: "center", mb: 3 }} color="green">
            {t("confirmSuccessfully")}
          </Typography>
          <Button title={t("continue")} onClick={handleContinue} />
          <MuiButton
            variant="text"
            color="warning"
            fullWidth
            sx={{ mt: 2 }}
            onClick={backToProfile}
          >
            {t("backToProfile")}
          </MuiButton>
        </div>
      ) : (
        <div>
          <Typography sx={{ textAlign: "center", mb: 2 }} variant="body2">
            {t("checkInbox")}
          </Typography>
          <Typography sx={{ textAlign: "center" }}>{t("emailSent")}</Typography>
          <Typography sx={{ textAlign: "center" }}>{email}</Typography>
          <Formik
            validateOnChange
            initialValues={{ email: "", password: "" }}
            onSubmit={(data: any) => handleSubmit(data)}
            validationSchema={validationSchema}
          >
            {() => (
              <Form style={{ textAlign: "left" }} noValidate autoComplete="off">
                <Box>
                  <AppTextField
                    placeholder={t("enterCode")}
                    name="code"
                    label={t("enterCode")}
                  />
                </Box>
                <AppSubmitButton
                  label={t("continue")}
                  disabled={false}
                  loading={false}
                />
              </Form>
            )}
          </Formik>
          <div style={{ fontSize: "14px", display: "flex", marginTop: "10px" }}>
            <span style={{ marginRight: "5px", marginTop: "5px" }}>
              {t("ifDontReceive")}
            </span>
            <MuiButton variant="text" onClick={() => resendOtp()}>
              <text.T14
                style={{
                  color: theme.colors.accentColor,
                  marginBottom: "10px",
                }}
              >
                {t("requestNewCode")}
              </text.T14>
            </MuiButton>
          </div>
          <Link to={APP_ROUTES.DEPOSIT} style={{ textDecoration: "none" }}>
            <text.T16
              style={{
                color: theme.colors.accentColor,
                marginBottom: "10px",
                textAlign: "center",
              }}
            >
              {t("skipThisStep")}
            </text.T16>
          </Link>
        </div>
      )}
    </div>
  );
};

export default EmailVerificationStep;
